import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { StateTermsFields, useCompliance, complianceReady } from "../components/StateTermsFields";
import { CodeStep } from "../components/CodeStep";
import { GoogleButton } from "../components/GoogleButton";

const IMG = "https://images.unsplash.com/photo-1670811456186-e73d0ace9454?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function AuthPage({ mode }) {
  const isLogin = mode === "login";
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [err, setErr] = useState("");
  const compliance = useCompliance();
  const [legal, setLegal] = useState({ state: "", age_confirmed: false, accept_terms: false, intermediary_ack: false });
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [params] = useSearchParams();
  const refCode = params.get("ref") || localStorage.getItem("vq_ref") || "";
  const [invite, setInvite] = useState(null);
  useEffect(() => {
    if (!refCode) return;
    localStorage.setItem("vq_ref", refCode);
    api.get(`/referral/${refCode}`).then((r) => setInvite(r.data.valid ? r.data : null));
  }, [refCode]);

  const go = (u) => {
    toast.success(isLogin ? "Welcome back" : "Account created — claim your spot");
    nav(u.role === "admin" ? "/admin" : u.role === "broker" ? "/broker" : u.role === "coo" ? "/coo" : "/dashboard");
  };

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const u = isLogin ? await login({ email: form.email, password: form.password }) : await register({ ...form, ...legal, referral_code: invite ? refCode : undefined });
      if (u.mfa) setChallenge(u.mfa); else go(u);
    } catch (e2) {
      setErr(formatApiError(e2));
    } finally { setBusy(false); }
  };

  const field = (k, label, type = "text") => (
    <label className="block">
      <span className="eyebrow !text-slate-400">{label}</span>
      <input data-testid={`auth-${k}-input`} type={type} required value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="lux-input mt-2" />
    </label>
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src={IMG} alt="Sunset resort" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F19] via-[#0B0F19]/30 to-transparent" />
        <p className="absolute bottom-16 left-12 right-12 font-serif-lux text-4xl leading-tight">"Every month in line is a month closer to the shore."</p>
      </div>
      <div className="flex items-center px-6 md:px-16 pt-28 pb-16">
        {challenge ? <CodeStep challenge={challenge} onDone={go} onCancel={() => setChallenge(null)} /> : (
        <form onSubmit={submit} className="w-full max-w-md space-y-6 rise" data-testid="auth-form">
          <p className="eyebrow">{isLogin ? "Members" : "New member"}</p>
          <h1 className="font-serif-lux text-4xl sm:text-5xl">{isLogin ? "Sign in" : "Join the club"}</h1>
          {!isLogin && invite && (
            <div data-testid="invite-banner" className="lux-card p-4 flex items-center gap-3 border-[#38BDF8]/40">
              <Sparkles className="w-5 h-5 text-[#38BDF8]" />
              <p className="text-sm">{invite.referrer} invited you — you'll get <b>{invite.discount_pct}% off</b> every membership.</p>
            </div>
          )}
          <GoogleButton label={isLogin ? "Sign in with Google" : "Join with Google"} />
          {!isLogin && field("name", "Full name")}
          {field("email", "Email", "email")}
          {field("password", "Password", "password")}
          {err && <p data-testid="auth-error" className="text-sm text-red-400">{err}</p>}
          {!isLogin && <StateTermsFields value={legal} onChange={setLegal} compliance={compliance} />}
          <button data-testid="auth-submit-btn" disabled={busy || (!isLogin && !complianceReady(legal, compliance))} className="btn-gold w-full py-3.5">
            {busy ? "Please wait…" : isLogin ? "Sign in" : "Create account"}
          </button>
          <p className="text-sm text-slate-400">
            {isLogin ? "New here? " : "Already a member? "}
            <Link to={isLogin ? "/register" : "/login"} data-testid="auth-switch-link" className="text-[#38BDF8] hover:underline">
              {isLogin ? "Create an account" : "Sign in"}
            </Link>
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
